import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for auto-refresh functionality in SOC Dashboard
 * Implements countdown timer and automatic data refresh at specified intervals
 * 
 * @param {Function} refreshCallback - Callback function to execute on refresh
 * @param {number} intervalMinutes - Refresh interval in minutes (default: 2)
 * @param {boolean} initialPauseState - Initial pause state (default: false)
 * @returns {Object} Refresh state and control functions
 */
const useAutoRefresh = (
  refreshCallback,
  intervalMinutes = 2,
  initialPauseState = false
) => {
  // State for pause/resume functionality
  const [isPaused, setIsPaused] = useState(initialPauseState);
  
  // State for countdown timer (in seconds)
  const [secondsRemaining, setSecondsRemaining] = useState(intervalMinutes * 60);
  
  // State for connection status
  const [connectionStatus, setConnectionStatus] = useState('online'); // 'online', 'updating', 'offline'
  
  // State for last refresh timestamp
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());

  // Ref to store the refresh interval timer
  const refreshIntervalRef = useRef(null);
  
  // Ref to store the countdown interval timer
  const countdownIntervalRef = useRef(null);

  /**
   * Executes the refresh callback and updates related state
   */
  const executeRefresh = useCallback(async () => {
    if (!refreshCallback || isPaused) return;

    try {
      setConnectionStatus('updating');
      await refreshCallback();
      setConnectionStatus('online');
      setLastRefreshTime(new Date());
      setSecondsRemaining(intervalMinutes * 60); // Reset countdown
    } catch (error) {
        Sentry.captureException(error);
      console.error('Auto-refresh failed:', error);
      setConnectionStatus('offline');
      // Still reset countdown even on error to prevent rapid retry attempts
      setSecondsRemaining(intervalMinutes * 60);
    }
  }, [refreshCallback, isPaused, intervalMinutes]);

  /**
   * Manually triggers a refresh
   */
  const triggerRefresh = useCallback(() => {
    executeRefresh();
  }, [executeRefresh]);

  /**
   * Toggles pause state
   */
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  /**
   * Pauses auto-refresh
   */
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  /**
   * Resumes auto-refresh
   */
  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  /**
   * Sets up countdown timer - decrements every second
   * Note: The actual refresh is triggered by the refresh interval timer in a separate useEffect.
   * This countdown provides visual feedback to users about when the next refresh will occur.
   */
  useEffect(() => {
    if (isPaused) {
      // Clear countdown when paused
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Start countdown
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        const nextValue = prev - 1;
        // Reset to full interval when reaching 0
        // The actual refresh is handled by the refresh interval timer
        if (nextValue <= 0) {
          return intervalMinutes * 60;
        }
        return nextValue;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isPaused, intervalMinutes]);

  /**
   * Sets up auto-refresh interval
   */
  useEffect(() => {
    if (isPaused) {
      // Clear refresh interval when paused
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Set up refresh interval
    refreshIntervalRef.current = setInterval(() => {
      executeRefresh();
    }, intervalMinutes * 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isPaused, intervalMinutes, executeRefresh]);

  /**
   * Formats seconds remaining into MM:SS format
   */
  const formatCountdown = useCallback(() => {
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [secondsRemaining]);

  /**
   * Calculates progress percentage for visual countdown
   */
  const getProgressPercentage = useCallback(() => {
    const totalSeconds = intervalMinutes * 60;
    return ((totalSeconds - secondsRemaining) / totalSeconds) * 100;
  }, [secondsRemaining, intervalMinutes]);

  return {
    isPaused,
    secondsRemaining,
    connectionStatus,
    lastRefreshTime,
    countdownDisplay: formatCountdown(),
    progressPercentage: getProgressPercentage(),
    togglePause,
    pause,
    resume,
    triggerRefresh
  };
};

export default useAutoRefresh;
